import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonType } from "devextreme/common";
import { Button, Popup } from "devextreme-react";
import Form, { ButtonItem, FormRef, GroupItem, Label, RequiredRule, SimpleItem, StringLengthRule } from "devextreme-react/form";
import { SurchargeDetailForEdit } from "../../../../shared/models/SurchargeDetailForEdit";
import { useCustomerAttributeConstraint } from "../../../../hooks/useCustomerAttributeConstraint";
import { CustomerAttributeConstraintType } from "../../../../shared/enums";
import { ReviewNewChargesApi } from "../../../../shared/api/ReviewNewCharges.api";
import { useFormCloseConfirm } from "@/hooks/useFormCloseConfirm";
import { store } from "@/store/store";
import { setToastInfo } from "@/redux/shareSlice";

interface Props {
  visible: boolean;
  surchargeDetail: SurchargeDetailForEdit;
  closeForm: (reloadData: boolean) => Promise<void>;
}

const SurchargeCustomerEditForm = ({ visible, surchargeDetail, closeForm }: Props) => {
  const formRef = useRef<FormRef>(null);
  const { getCustomerAttributeConstraintsByType } = useCustomerAttributeConstraint();
  const { setInitialFormData, canCloseForm } = useFormCloseConfirm({} as SurchargeDetailForEdit);

  const [formData, setFormData] = useState({} as SurchargeDetailForEdit);

  const noApprovedBy = useMemo(() => surchargeDetail.approvedBy === null, [surchargeDetail.approvedBy]);
  const salesOrgEditorOperations = useMemo(
    () => getCustomerAttributeConstraintsByType(CustomerAttributeConstraintType.SalesOrg),
    [getCustomerAttributeConstraintsByType]
  );
  const sapMaterialProductCodeEditorOperations = useMemo(
    () => getCustomerAttributeConstraintsByType(CustomerAttributeConstraintType.SAPMaterialProductCode),
    [getCustomerAttributeConstraintsByType]
  );
  const sapEnergySurchargeCodeEditorOperations = useMemo(
    () => getCustomerAttributeConstraintsByType(CustomerAttributeConstraintType.SAPEnergySurchargeCode),
    [getCustomerAttributeConstraintsByType]
  );
  const uomEditorOperations = useMemo(
    () => getCustomerAttributeConstraintsByType(CustomerAttributeConstraintType.UOM),
    [getCustomerAttributeConstraintsByType]
  );

  useEffect(() => {
    if (!surchargeDetail) {
      return;
    }

    setFormData(surchargeDetail);
    setInitialFormData(surchargeDetail);
  }, [surchargeDetail]);

  const handleCancel = useCallback(
    async (checkEdit: boolean) => {
      if (checkEdit && noApprovedBy && !canCloseForm(formData)) {
        return;
      }

      closeForm && (await closeForm(false));
    },
    [canCloseForm, closeForm, formData, noApprovedBy]
  );

  const handleRevokeApproval = useCallback(async () => {
    if (await ReviewNewChargesApi.revokeApproval(formData.surchargeEventCalculationResultId)) {
      store.dispatch(
        setToastInfo(
          `Approval has been revoked for '${formData.name}', Ship To ${formData.shipTo}.\r\n\r\nThis record will not be included in the export data.`
        )
      );
      await handleCancel(false);
    }
  }, [formData.name, formData.shipTo, formData.surchargeEventCalculationResultId, handleCancel]);

  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();
      if (await ReviewNewChargesApi.applyCalculationResultChanges(formData)) {
        closeForm && (await closeForm(true));
      }
    },
    [formData, closeForm]
  );

  return (
    <Popup
      width={850}
      height="auto"
      showTitle={true}
      title={"Edit Surcharge Increase"}
      dragEnabled={true}
      hideOnOutsideClick={false}
      visible={visible}
      onHiding={async (e) => {
        e.cancel = true;
        await handleCancel(true);
      }}
      showCloseButton={true}
    >
      <form onSubmit={handleSubmit}>
        <Form ref={formRef} labelMode="floating" formData={formData} colCount={2} showColonAfterLabel={true}>
          <GroupItem>
            <SimpleItem
              label={{ text: "SAP ShipTo #:" }}
              dataField={"shipTo"}
              editorType="dxTextBox"
              editorOptions={{
                disabled: true,
              }}
            ></SimpleItem>
            <SimpleItem label={{ text: "Primary Sales Rep:" }} dataField={"primarySalesRep"} editorType="dxTextBox">
              <RequiredRule message="Primary Sales Rep is required." />
            </SimpleItem>

            <SimpleItem
              label={{ text: "SAP Material Product Code:" }}
              dataField="sapMaterialProductCode"
              editorType="dxSelectBox"
              editorOptions={{ dataSource: sapMaterialProductCodeEditorOperations }}
            >
              <RequiredRule message="SAP Material Product Code is required." />
            </SimpleItem>

            <SimpleItem
              label={{ text: "Access Sequence:" }}
              dataField={"accessSequence"}
              editorType="dxTextBox"
              editorOptions={{
                disabled: true,
              }}
            ></SimpleItem>

            <SimpleItem label={{ text: "Unit:" }} dataField={"unit"} editorType="dxNumberBox">
              <RequiredRule message="Unit is required." />
              <StringLengthRule max={35} message="The data lengh should less than 35." />
            </SimpleItem>

            <SimpleItem
              dataField="surchargeEffectiveFrom"
              editorType="dxDateBox"
              editorOptions={{
                displayFormat: "yyyy-MM-dd",
                type: "date",
                placeholder: "Valid From",
                acceptCustomValue: true,
                openOnFieldClick: true,
                valueFormat: "yyyyMMdd",
                dateSerializationFormat: "yyyyMMdd",
              }}
            >
              <Label text="Valid From:" />
              <RequiredRule message="Valid From is required" />
            </SimpleItem>
          </GroupItem>

          <GroupItem>
            <SimpleItem label={{ text: "Sales Org:" }} dataField="salesOrg" editorType="dxSelectBox" editorOptions={{ dataSource: salesOrgEditorOperations }}>
              <RequiredRule message="Sales Org is required." />
            </SimpleItem>

            <SimpleItem label={{ text: "Customer Name" }} dataField={"name"} editorType="dxTextBox">
              <RequiredRule message="Customer Name is required." />
            </SimpleItem>

            <SimpleItem
              label={{ text: "SAP Energy Surcharge Code:" }}
              dataField="sapEnergySurchargeCode"
              editorType="dxSelectBox"
              editorOptions={{ dataSource: sapEnergySurchargeCodeEditorOperations }}
            >
              <RequiredRule message="SAP Energy Surcharge Code is required." />
            </SimpleItem>

            <SimpleItem label={{ text: "New Energy Surcharge Rate:" }} dataField={"surcharge"} editorType="dxTextBox">
              <RequiredRule message="New Energy Surcharge Rate is required." />
              <StringLengthRule max={35} message="The data lengh should less than 35." />
            </SimpleItem>

            <SimpleItem label={{ text: "UOM:" }} dataField="uom" editorType="dxSelectBox" editorOptions={{ dataSource: uomEditorOperations }}>
              <RequiredRule message="UOM is required." />
            </SimpleItem>
            <SimpleItem
              dataField="surchargeEffectiveTo"
              editorType="dxDateBox"
              editorOptions={{
                displayFormat: "yyyy-MM-dd",
                type: "date",
                placeholder: "Valid From",
                acceptCustomValue: true,
                openOnFieldClick: true,
                valueFormat: "yyyyMMdd",
                dateSerializationFormat: "yyyyMMdd",
              }}
            >
              <Label text="Valid To:" />
              <RequiredRule message="Valid To is required" />
            </SimpleItem>
          </GroupItem>

          <GroupItem colSpan={2}>
            <SimpleItem label={{ text: "Comment" }} dataField={"comment"} editorType="dxTextBox">
              <RequiredRule message="Comment is required." />
            </SimpleItem>
          </GroupItem>

          <GroupItem colSpan={2}>
            <span className="me-2">Changed By: {formData.updatedBy}</span>
            <span>Changed On: {formData.updatedAt}</span>
          </GroupItem>

          <GroupItem colSpan={2}>
            {formData.approvedBy ? (
              <SimpleItem>
                <span className="me-2">Approved By: {formData.approvedBy}</span>
                <span className="me-2">Approved At: {formData.approvedAt}</span>
                <Button type={"danger"} onClick={handleRevokeApproval}>
                  Revoke Approval
                </Button>
              </SimpleItem>
            ) : null}
          </GroupItem>

          <GroupItem
            colSpan={2}
            cssClass="buttons-group mt-4"
            colCountByScreen={
              noApprovedBy
                ? {
                    xs: 2,
                    sm: 2,
                    md: 2,
                    lg: 2,
                  }
                : {
                    xs: 1,
                    sm: 1,
                    md: 1,
                    lg: 1,
                  }
            }
          >
            {noApprovedBy ? (
              <ButtonItem
                buttonOptions={{
                  text: "SAVE",
                  type: "default" as ButtonType,
                  useSubmitBehavior: true,
                  width: "120px",
                }}
              />
            ) : null}

            <ButtonItem
              buttonOptions={{
                disabled: false,
                text: "CANCEL",
                width: "120px",
                onClick: () => handleCancel(true),
              }}
              name="Cancel"
            />
          </GroupItem>
        </Form>
      </form>
    </Popup>
  );
};

export default SurchargeCustomerEditForm;
